import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonTextarea,
  IonGrid,
  IonRow,
  IonCol,
  IonSpinner,
} from '@ionic/react';
import { checkmarkCircle, warning, closeCircle, terminal } from 'ionicons/icons';
import './ControlPage.css';

// Dummy-API für RAM und Disk (später von freak_admin holen)
const fetchSystemStats = async () => {
  // Simulierte Werte
  return {
    memory: {
      total: 16000, // MB
      available: 6200, // MB
    },
    disk: {
      df: `Filesystem      Size  Used Avail Use% Mounted on\n/dev/sda1        100G   60G   36G  63% /\ntmpfs            7.8G     0  7.8G   0% /dev/shm\n`,
    },
  };
};

// Dummy API calls (to be replaced with real backend calls)
const fetchDockerStates = async () => {
  // Should return [{name: 'freak_ranker', status: 'green', logs: '...'}, ...]
  // uptimeHistory: Array<boolean> (true=up, false=down), simuliere für Demo
  // Simuliere 7 Tage à 24 Stunden = 168 Einträge
  const genHistory = (upProb = 0.95) => Array.from({length: 168}, (_, i) => Math.random() < upProb);
  return [
    { name: 'freak_ranker', status: 'green', logs: 'Ranker running...\nOK', uptimeHistory: genHistory(0.99), stats: { requests: 1200, codes: { 200: 1150, 400: 30, 404: 10, 500: 10 } } },
    { name: 'freak_videoservice', status: 'yellow', logs: 'VideoService: Restarting...', uptimeHistory: genHistory(0.95), stats: { requests: 800, codes: { 200: 700, 400: 50, 404: 30, 500: 20 } } },
    { name: 'freak_classifier', status: 'red', logs: 'Classifier crashed!\nTraceback...', uptimeHistory: genHistory(0.85), stats: { requests: 500, codes: { 200: 400, 400: 50, 404: 20, 500: 30 } } },
    { name: 'freak_buildbot', status: 'green', logs: 'Buildbot idle.', uptimeHistory: genHistory(0.99), stats: { requests: 100, codes: { 200: 100 } } },
    { name: 'freak_app', status: 'green', logs: 'App läuft.', uptimeHistory: genHistory(0.995), stats: { requests: 2000, codes: { 200: 1980, 400: 10, 404: 5, 500: 5 } } },
    { name: 'freak_api', status: 'green', logs: 'API bereit.', uptimeHistory: genHistory(0.995), stats: { requests: 3000, codes: { 200: 2950, 400: 20, 404: 10, 500: 20 } } },
    { name: 'freak_estimator', status: 'yellow', logs: 'Estimator: Initialisierung...', uptimeHistory: genHistory(0.9), stats: { requests: 600, codes: { 200: 550, 400: 20, 404: 10, 500: 20 } } },
  ];
};

const fetchConfusionMatrix = async () => {
  // Should return a 2D array or similar structure
  return {
    labels: ['A', 'B', 'C'],
    matrix: [
      [10, 2, 1],
      [1, 8, 3],
      [0, 2, 12],
    ],
  };
};

const ControlPage: React.FC = () => {
  const [dockerStates, setDockerStates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [confMatrix, setConfMatrix] = useState<any>(null);
  const [training, setTraining] = useState<'idle' | 'running' | 'error'>('idle');
  const [trainLog, setTrainLog] = useState('');
  const [buildbotLog, setBuildbotLog] = useState('');
  const [cleaning, setCleaning] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [systemStats, setSystemStats] = useState<any>(null);
  useEffect(() => {
    // Poll system stats every 10s
    const poll = async () => {
      const stats = await fetchSystemStats();
      setSystemStats(stats);
    };
    poll();
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Poll docker states every 5s
    const poll = async () => {
      setLoading(true);
      const states = await fetchDockerStates();
      setDockerStates(states);
      setLoading(false);
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Poll confusion matrix every 10s
    const poll = async () => {
      const cm = await fetchConfusionMatrix();
      setConfMatrix(cm);
    };
    poll();
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, []);

  // Dummy handlers
  const handleStartTraining = () => {
    setTraining('running');
    setTrainLog('Training started...\n');
    // TODO: call backend
  };
  const handleStopTraining = () => {
    setTraining('idle');
    setTrainLog((l) => l + '\nTraining stopped.');
    // TODO: call backend
  };

  const handleCleanBuildbot = () => {
    setCleaning(true);
    setTimeout(() => {
      setBuildbotLog((l) => l + '\nclean.sh ausgeführt.');
      setCleaning(false);
    }, 2000);
    // TODO: call backend
  };

  const handleRebuildBuildbot = () => {
    setRebuilding(true);
    setTimeout(() => {
      setBuildbotLog((l) => l + '\nbuildbot rebuilt.');
      setRebuilding(false);
    }, 3000);
    // TODO: call backend
  };

  const statusIcon = (status: string) => {
    if (status === 'green') return <IonIcon color="success" icon={checkmarkCircle} />;
    if (status === 'yellow') return <IonIcon color="warning" icon={warning} />;
    return <IonIcon color="danger" icon={closeCircle} />;
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Admin Dashboard</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonCard style={{ marginBottom: 32 }}>
          <IonCardHeader>
          <IonCardTitle className="white-title">Docker Services</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {loading ? (
              <IonSpinner />
            ) : (
              <IonList>
                {dockerStates.map((svc) => {
                  // Uptime Prozent berechnen
                  const history = svc.uptimeHistory || [];
                  const upCount = history.filter(Boolean).length;
                  const percent = history.length > 0 ? Math.round((upCount / history.length) * 100) : 0;
                  return (
                    <IonItem key={svc.name}>
                      {statusIcon(svc.status)}
                      <IonLabel className="ion-margin-start">
                        <b>{svc.name}</b>
                        <div style={{ fontSize: 12, margin: '4px 0 4px 0', width: '100%' }}>
                          Uptime: {percent}%
                          <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            minHeight: 18,
                            marginTop: 2,
                            background: '#222',
                            borderRadius: 3,
                            overflow: 'hidden',
                          }}>
                            {history.map((up: boolean, i: number) => {
                              // 168 Balken, je 1h, von alt (links) nach neu (rechts)
                              const now = new Date();
                              const time = new Date(now.getTime() - (history.length - 1 - i) * 60 * 60 * 1000);
                              const label = time.toLocaleDateString([], { month: '2-digit', day: '2-digit' }) + ' ' + time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                              return (
                                <div
                                  key={i}
                                  title={label + (up ? ' (up)' : ' (down)')}
                                  style={{
                                    flex: 1,
                                    background: up ? '#0f0' : '#f00',
                                    height: 18,
                                    minWidth: 0,
                                    transition: 'background 0.3s',
                                  }}
                                />
                              );
                            })}
                          </div>
                          <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'#aaa',marginTop:2}}>
                            <span>{(() => {
                              const now = new Date();
                              const first = new Date(now.getTime() - (history.length - 1) * 60 * 60 * 1000);
                              return first.toLocaleDateString([], { month: '2-digit', day: '2-digit' }) + ' ' + first.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            })()}</span>
                            <span>{(() => {
                              const now = new Date();
                              return now.toLocaleDateString([], { month: '2-digit', day: '2-digit' }) + ' ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            })()}</span>
                          </div>
                        </div>
                        <div style={{ fontSize: 12, margin: '4px 0 4px 0' }}>
                          <b>Requests:</b> {svc.stats?.requests ?? '-'}
                          {svc.stats?.codes && (
                            <span style={{ marginLeft: 8 }}>
                              {Object.entries(svc.stats.codes).map(([code, count]) => (
                                <span key={code} style={{ marginRight: 8 }}>
                                  <span style={{ color: code === '200' ? '#0f0' : code === '400' ? '#ff0' : code === '404' ? '#0ff' : '#f00' }}>
                                    {code}
                                  </span>
                                  : {count as number}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                        <IonTextarea
                          value={svc.logs}
                          readonly
                          autoGrow
                          style={{ fontFamily: 'monospace', fontSize: 12, background: '#222', color: '#fff', marginTop: 8 }}
                        />
                      </IonLabel>
                    </IonItem>
                  );
                })}
              </IonList>
            )}
          </IonCardContent>
        </IonCard>

        <IonCard style={{ marginBottom: 32 }}>
          <IonCardHeader>
          <IonCardTitle className="white-title">Training: freak_classifier</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonButton color="success" onClick={handleStartTraining} disabled={training === 'running'}>Training starten</IonButton>
            <IonButton color="danger" onClick={handleStopTraining} disabled={training !== 'running'}>Training stoppen</IonButton>
            <IonTextarea
              value={trainLog}
              readonly
              autoGrow
              style={{ fontFamily: 'monospace', fontSize: 12, background: '#222', color: '#fff', marginTop: 8 }}
            />
            <div style={{ marginTop: 16 }}>
              <b>Status:</b> {training === 'running' ? 'Läuft...' : training === 'idle' ? 'Bereit' : 'Fehler'}
            </div>
            <div style={{ marginTop: 16 }}>
              <b>Confusion Matrix:</b>
              {confMatrix ? (
                <IonGrid>
                  <IonRow>
                    <IonCol></IonCol>
                    {confMatrix.labels.map((l: string) => (
                      <IonCol key={l}><b>{l}</b></IonCol>
                    ))}
                  </IonRow>
                  {confMatrix.matrix.map((row: number[], i: number) => (
                    <IonRow key={i}>
                      <IonCol><b>{confMatrix.labels[i]}</b></IonCol>
                      {row.map((val: number, j: number) => (
                        <IonCol key={j}>{val}</IonCol>
                      ))}
                    </IonRow>
                  ))}
                </IonGrid>
              ) : (
                <IonSpinner />
              )}
            </div>
          </IonCardContent>
        </IonCard>

        <IonCard style={{ marginBottom: 32 }}>
          <IonCardHeader>
            <IonCardTitle className="white-title">Buildbot: freak_buildbot</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonButton color="medium" onClick={handleCleanBuildbot} disabled={cleaning}>
              <IonIcon icon={terminal} slot="start" />clean.sh ausführen
            </IonButton>
            <IonButton color="warning" onClick={handleRebuildBuildbot} disabled={rebuilding} style={{ marginLeft: 8 }}>
              <IonIcon icon={terminal} slot="start" />rebuild buildbot
            </IonButton>
            <IonTextarea
              value={buildbotLog}
              readonly
              autoGrow
              style={{ fontFamily: 'monospace', fontSize: 12, background: '#222', color: '#fff', marginTop: 8 }}
            />
            <div style={{marginTop:16}}>
              <b>Speicher (RAM):</b>
              {systemStats ? (
                <div style={{width: '100%', maxWidth: 400, margin: '8px 0'}}>
                  <div style={{background:'#333', borderRadius:4, height:24, position:'relative'}}>
                    <div style={{
                      width: `${Math.round((systemStats.memory.total-systemStats.memory.available)/systemStats.memory.total*100)}%`,
                      background:'#f90',
                      height:'100%',
                      borderRadius:4,
                      transition:'width 0.5s'
                    }} />
                    <div style={{position:'absolute',left:8,top:2,color:'#fff',fontSize:13}}>
                      {systemStats.memory.total-systemStats.memory.available} MB verwendet
                    </div>
                    <div style={{position:'absolute',right:8,top:2,color:'#fff',fontSize:13}}>
                      {systemStats.memory.available} MB frei
                    </div>
                  </div>
                  <div style={{color:'#fff',fontSize:12,marginTop:2}}>
                    Gesamt: {systemStats.memory.total} MB
                  </div>
                </div>
              ) : (
                <IonSpinner />
              )}
            </div>
            <div style={{marginTop:16}}>
              <b>Festplatte (df -h):</b>
              {systemStats ? (
                <pre style={{background:'#222',color:'#fff',fontSize:12,padding:8,borderRadius:4,marginTop:4}}>
                  {systemStats.disk.df}
                </pre>
              ) : (
                <IonSpinner />
              )}
            </div>
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  );
};

export default ControlPage;
